/**
 * Bundle batch results into a ZIP entirely client-side (spec §4.2.8). `fflate`
 * is dynamically imported so it stays in its own lazy chunk — the Free tier
 * (and the initial bundle budget) never pays for it; only entering Batch and
 * exporting loads it.
 */
import type { BatchFileResult } from './types';

/**
 * Sanitize a user-derived output name into a safe ZIP entry name (no zip-slip
 * for downstream extractors): keep only the basename — everything after the
 * last `/` or `\` — then strip NUL bytes and any remaining `..` sequences.
 * Falls back to `file` if nothing survives.
 */
export function sanitizeEntryName(name: string): string {
  const base = name.split(/[/\\]/).pop() ?? '';
  const clean = base.replaceAll('\0', '').replaceAll('..', '');
  return clean.length > 0 ? clean : 'file';
}

/** Append a numeric suffix to a name (before its extension) to make it unique. */
function disambiguate(name: string, taken: Set<string>): string {
  if (!taken.has(name)) return name;
  const dot = name.lastIndexOf('.');
  const base = dot < 0 ? name : name.slice(0, dot);
  const ext = dot < 0 ? '' : name.slice(dot);
  let n = 2;
  let candidate = `${base}-${n}${ext}`;
  while (taken.has(candidate)) {
    n += 1;
    candidate = `${base}-${n}${ext}`;
  }
  return candidate;
}

/** Build a ZIP (level-6 deflate) of every successful result, keyed by output name. */
export async function buildZip(results: BatchFileResult[]): Promise<Uint8Array> {
  const { zipSync } = await import('fflate');
  const taken = new Set<string>();
  const files: Record<string, Uint8Array> = {};
  for (const r of results) {
    if (r.status !== 'ok' || !r.bytes || !r.outputName) continue;
    const name = disambiguate(sanitizeEntryName(r.outputName), taken);
    taken.add(name);
    files[name] = r.bytes;
  }
  return zipSync(files, { level: 6 });
}
